import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';

// ─── Sites ──────────────────────────────────────────────────────────────────
export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => api('/sites').then((res) => res.sites),
    staleTime: 60 * 60 * 1000,
  });
}

export function useEventTypes(siteCode) {
  return useQuery({
    queryKey: ['event-types', siteCode],
    queryFn: () => api(`/sites/${siteCode}/event-types`).then((res) => res.eventTypes),
    enabled: !!siteCode,
    staleTime: 60 * 60 * 1000,
  });
}

// Textes globaux éditables (bannière d'accueil, etc.).
export function useContent() {
  return useQuery({
    queryKey: ['content'],
    queryFn: () => api('/content').then((res) => res.settings || {}),
    staleTime: 60 * 60 * 1000,
  });
}

// ─── Availability ───────────────────────────────────────────────────────────
// L'API renvoie { slots: [{ startTime, endTime, status: 'available'|'full', spotsLeft? }] }.
// On normalise vers la forme attendue par AvailabilitySlot : { time, endTime, available, remaining }.
export function useAvailability(params) {
  const { site, eventType, date } = params || {};
  return useQuery({
    queryKey: ['availability', site, eventType, date],
    queryFn: () =>
      api(`/availability?site=${site}&eventType=${eventType}&date=${date}`).then((res) => ({
        slots: (res.slots || []).map((s) => ({
          time: s.startTime,
          endTime: s.endTime,
          available: s.status === 'available',
          remaining: s.spotsLeft ?? null,
        })),
      })),
    enabled: !!site && !!eventType && !!date,
    staleTime: 3000,
  });
}

// ─── Reservation ────────────────────────────────────────────────────────────
export function useCreateReservation() {
  return useMutation({
    mutationFn: (payload) => api('/reservations', { method: 'POST', body: payload }),
  });
}

export function useReservationByCode(code, email) {
  return useQuery({
    queryKey: ['reservation', code],
    queryFn: () => api(`/reservations/${code}${email ? `?email=${encodeURIComponent(email)}` : ''}`),
    enabled: !!code,
  });
}

// ─── Payments ───────────────────────────────────────────────────────────────
export function useInitiatePayment() {
  return useMutation({
    mutationFn: (payload) => api('/payments/initiate', { method: 'POST', body: payload }),
  });
}

// ─── Account ────────────────────────────────────────────────────────────────
export function useMyReservations(page = 1) {
  return useQuery({
    queryKey: ['my-reservations', page],
    queryFn: () => api(`/account/reservations?page=${page}`),
  });
}

export function useLoyalty(page = 1) {
  return useQuery({
    queryKey: ['loyalty', page],
    queryFn: () => api(`/account/loyalty?page=${page}`),
  });
}

export function useRedeem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api('/account/loyalty/redeem', { method: 'POST', body: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty'] });
    },
  });
}

// ─── Password reset ─────────────────────────────────────────────────────────
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (payload) => api('/auth/customer/request-reset', { method: 'POST', body: payload }),
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (payload) => api('/auth/customer/reset-password', { method: 'POST', body: payload }),
  });
}
